export const CURTAIN_ID = "cinematic-curtain";

let cutInProgress = false;
let lockedSection: string | null = null;
let safetyTimer: number | undefined;
let stopWatching: (() => void) | undefined;

/** The section a programmatic scroll is heading to, if one is running. */
export function getLockedSection() {
  return lockedSection;
}

function releaseLock() {
  lockedSection = null;
  stopWatching?.();
  stopWatching = undefined;
  window.clearTimeout(safetyTimer);
}

/**
 * Pins the active section while a navigation scroll runs, so the scroll-spy
 * doesn't report the sections passed on the way and make the nav flicker.
 */
export function lockSection(id: string) {
  releaseLock();
  lockedSection = id;
  // Safety net in case the scroll never starts.
  safetyTimer = window.setTimeout(releaseLock, 2500);
}

/** Releases the lock once scrolling has gone idle. */
function releaseWhenSettled() {
  stopWatching?.();
  let idle = window.setTimeout(releaseLock, 180);
  const onScroll = () => {
    window.clearTimeout(idle);
    idle = window.setTimeout(releaseLock, 140);
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  stopWatching = () => {
    window.removeEventListener("scroll", onScroll);
    window.clearTimeout(idle);
  };
}

/**
 * Fade to black, run `action` (typically an instant scroll), fade back in.
 * Falls back to running the action directly if the curtain is missing.
 */
function cinematicCut(action: () => void) {
  const curtain = document.getElementById(CURTAIN_ID);
  if (!curtain || cutInProgress) {
    action();
    return;
  }

  cutInProgress = true;
  const fadeOut = curtain.animate([{ opacity: 0 }, { opacity: 1 }], {
    duration: 240,
    easing: "cubic-bezier(0.4, 0, 1, 1)",
    fill: "forwards",
  });

  fadeOut.onfinish = () => {
    action();
    window.requestAnimationFrame(() => {
      const fadeIn = curtain.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: 520,
        easing: "cubic-bezier(0.22, 1, 0.36, 1)",
        fill: "forwards",
      });
      fadeIn.onfinish = () => {
        cutInProgress = false;
      };
    });
  };
}

/**
 * Scroll to a section. Short hops glide; long jumps (which would otherwise
 * scrub through every scroll-driven scene in between) become a cut.
 */
export function scrollToSection(
  id: string,
  { animate }: { animate: boolean }
) {
  const section = document.getElementById(id);
  if (!section) return;
  lockSection(id);

  if (!animate) {
    section.scrollIntoView({ behavior: "auto", block: "start" });
    releaseWhenSettled();
    return;
  }

  const distance = Math.abs(section.getBoundingClientRect().top);
  if (distance < window.innerHeight * 1.5) {
    section.scrollIntoView({ behavior: "smooth", block: "start" });
    releaseWhenSettled();
  } else {
    cinematicCut(() => {
      section.scrollIntoView({ behavior: "auto", block: "start" });
      releaseWhenSettled();
    });
  }
}
